"use client";

import { type FC, useEffect, useMemo } from "react";
import {
  EnergyLabel,
  Flex,
  FormRow,
  Grid,
  NumberField,
  RadioCards,
  Select,
  Spacing,
  Text,
} from "@huub-web/ui";
import { slideHeadingDescriptionStyle } from "./BaseSlide.css";
import { useTranslation } from "react-i18next";
import {
  FormInputDisplayConfiguration,
  InputConfiguration,
} from "@huub-web/ui/services/inputs";

import { useMaximumMortgageWizard } from "../../../hooks/useMaximumMortgageWizard";
import { BaseSlide, type BaseSlideProps } from "./BaseSlide";
import { Illustration } from "../../Illustration";
import { twoColumnGridStyle } from "./BaseSlide.css";
import {
  energyLabels,
  type MortgagePurpose,
  type HouseInMind,
  type HouseProgress,
  type EnergyLabelValue,
} from "../../../types";

type PurposeSlideProps = BaseSlideProps;

type PurposeOption = {
  value: MortgagePurpose;
  labelKey: string;
  illustrationName: "houseLove" | "refinance" | "maintenance";
};

const ALL_PURPOSE_OPTIONS: PurposeOption[] = [
  {
    value: "finance",
    labelKey: "maxMortgageWizard.purpose.buyHouse",
    illustrationName: "houseLove",
  },
  {
    value: "refinance",
    labelKey: "maxMortgageWizard.purpose.refinance",
    illustrationName: "refinance",
  },
  {
    value: "renovate",
    labelKey: "maxMortgageWizard.purpose.renovate",
    illustrationName: "maintenance",
  },
];

/**
 * Purpose slide component for the mortgage wizard
 * Handles mortgage purpose selection with substeps
 *
 * ⚠️ When adding/removing conditional fields, update filterMaximumMortgageWizardValues.ts
 */
export const PurposeSlide: FC<PurposeSlideProps> = () => {
  const { t, i18n } = useTranslation();
  const { state, updateFormValues, flowConfig, bankOptions, banksLoading } =
    useMaximumMortgageWizard();

  const homeValueDescription = i18n.exists(
    "maxMortgageWizard.purpose.currentHomeValueDescription",
  )
    ? t("maxMortgageWizard.purpose.currentHomeValueDescription")
    : "";
  const mortgageDebtDescription = i18n.exists(
    "maxMortgageWizard.purpose.currentMortgageDebtDescription",
  )
    ? t("maxMortgageWizard.purpose.currentMortgageDebtDescription")
    : "";
  const { formValues } = state;
  const plurality = formValues.hasPartner ? "plural" : "singular";

  const allowedTypes = flowConfig?.allowedMortgageTypes;

  const purposeOptions = useMemo(() => {
    if (!allowedTypes || allowedTypes.length === 0) {
      return ALL_PURPOSE_OPTIONS;
    }
    const filtered = ALL_PURPOSE_OPTIONS.filter((option) =>
      allowedTypes.includes(option.value),
    );
    return filtered.length > 0 ? filtered : ALL_PURPOSE_OPTIONS;
  }, [allowedTypes]);

  useEffect(() => {
    if (
      purposeOptions.length === 1 &&
      formValues.purpose !== purposeOptions[0].value
    ) {
      updateFormValues({ purpose: purposeOptions[0].value });
    }
  }, [purposeOptions, formValues.purpose, updateFormValues]);

  const isSingleOption = purposeOptions.length === 1;

  return (
    <BaseSlide
      slideKey="purpose"
      title={
        isSingleOption
          ? undefined
          : t(`maxMortgageWizard.purpose.title.${plurality}`)
      }
      description={
        isSingleOption
          ? undefined
          : t(`maxMortgageWizard.purpose.description.${plurality}`)
      }
    >
      <FormRow
        id="wizard__purpose"
        data-field-key="purpose"
        variant="vertical"
        style={isSingleOption ? { display: "none" } : undefined}
      >
        <RadioCards
          name="purpose"
          value={formValues.purpose ?? ""}
          onChange={(value) =>
            updateFormValues({
              purpose: value as MortgagePurpose,
            })
          }
          options={purposeOptions.map((option) => ({
            value: option.value,
            label: t(`${option.labelKey}.${plurality}`),
            leading: <Illustration name={option.illustrationName} size="xs" />,
          }))}
          validation={["required"]}
        />
      </FormRow>

      {formValues.purpose === "finance" && (
        <Flex direction="column" gap="m">
          <Spacing />
          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t(`maxMortgageWizard.purpose.houseInMindQuestion.${plurality}`)}
            </Text>
          </Flex>
          <FormRow
            id="wizard__houseInMind"
            data-field-key="houseInMind"
            variant="vertical"
          >
            <RadioCards
              name={t("maxMortgageWizard.purpose.houseInMindName")}
              value={formValues.houseInMind ?? ""}
              onChange={(value) =>
                updateFormValues({
                  houseInMind: value as HouseInMind,
                })
              }
              options={[
                {
                  value: "existing",
                  label: t(
                    "maxMortgageWizard.result.houseInMindDescriptive.existing",
                  ),
                  leading: <Illustration name="myHouse" size="xs" />,
                },
                {
                  value: "newly-build",
                  label: t(
                    "maxMortgageWizard.result.houseInMindDescriptive.newly-build",
                  ),
                  leading: <Illustration name="building" size="xs" />,
                },
                {
                  value: "no",
                  label: t(
                    "maxMortgageWizard.result.houseInMindDescriptive.no",
                  ),
                  leading: <Illustration name="default" size="xs" />,
                },
              ]}
              validation={["required"]}
            />
          </FormRow>
        </Flex>
      )}

      {formValues.purpose === "finance" &&
        (formValues.houseInMind === "existing" ||
          formValues.houseInMind === "newly-build") && (
          <Flex direction="column" gap="m">
            <Spacing />
            <Flex direction="column" gap="xs">
              <Text as="h3" variant="displayS" bold>
                {t(
                  `maxMortgageWizard.purpose.purchaseProgressQuestion.${plurality}`,
                )}
              </Text>
            </Flex>
            <FormRow
              id="wizard__houseProgress"
              data-field-key="houseProgress"
              variant="vertical"
            >
              <RadioCards
                name={t("maxMortgageWizard.purpose.purchaseProgressName")}
                value={formValues.houseProgress}
                onChange={(value) =>
                  updateFormValues({
                    houseProgress: value as HouseProgress,
                  })
                }
                options={[
                  {
                    value: "viewing-planned",
                    label: t(
                      "maxMortgageWizard.purpose.purchaseProgress.viewingPlanned",
                    ),
                    leading: <Illustration name="houseAnalysis" size="xs" />,
                  },
                  {
                    value: "bid-released",
                    label: t(
                      "maxMortgageWizard.purpose.purchaseProgress.bidPlaced",
                    ),
                    leading: <Illustration name="calculation" size="xs" />,
                  },
                  {
                    value: "contract-signed",
                    label: t(
                      "maxMortgageWizard.purpose.purchaseProgress.contractSigned",
                    ),
                    leading: <Illustration name="certificate" size="xs" />,
                  },
                ]}
                validation={["required"]}
              />
            </FormRow>
          </Flex>
        )}

      {(formValues.purpose === "refinance" ||
        formValues.purpose === "renovate") && (
        <Flex direction="column" gap="m">
          <Spacing />
          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t("maxMortgageWizard.purpose.currentHomeValue")}
            </Text>
            {homeValueDescription && (
              <Text className={slideHeadingDescriptionStyle}>
                {homeValueDescription}
              </Text>
            )}
          </Flex>
          <Grid variant="none" className={twoColumnGridStyle}>
            <FormRow
              id="wizard__currentHouseMarketValue"
              data-field-key="currentHouseMarketValue"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.currentHouseMarketValue &&
                  formValues.currentHouseMarketValue !== ""
                    ? Number(formValues.currentHouseMarketValue)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    currentHouseMarketValue: value?.toString() ?? "",
                  })
                }
                validation={["required"]}
                name={t("maxMortgageWizard.purpose.marketValueName")}
                label={t("maxMortgageWizard.purpose.marketValueLabel")}
              />
            </FormRow>

            <FormRow
              id="wizard__currentHouseEnergyLabel"
              data-field-key="currentHouseEnergyLabel"
            >
              <Select
                value={formValues.currentHouseEnergyLabel ?? ""}
                onChange={(value) =>
                  updateFormValues({
                    currentHouseEnergyLabel: value as
                      | "unknown"
                      | EnergyLabelValue,
                  })
                }
                options={[
                  {
                    label: t("maxMortgageWizard.purpose.unknown"),
                    value: "unknown",
                  },
                  ...energyLabels.map((label) => ({
                    label: (
                      <EnergyLabel
                        label={label}
                        size="s"
                        justifySelf="flex-start"
                      />
                    ),
                    value: label,
                  })),
                ]}
                validation={["required"]}
                name={t("maxMortgageWizard.purpose.energyLabelName")}
                label={t("maxMortgageWizard.purpose.energyLabelLabel")}
              />
            </FormRow>
          </Grid>

          <Spacing />

          <Flex direction="column" gap="xs">
            <Text as="h3" variant="displayS" bold>
              {t("maxMortgageWizard.purpose.currentMortgageDebt")}
            </Text>
            {mortgageDebtDescription && (
              <Text className={slideHeadingDescriptionStyle}>
                {mortgageDebtDescription}
              </Text>
            )}
          </Flex>
          <Grid variant="none" className={twoColumnGridStyle}>
            <FormRow
              id="wizard__currentHouseMortgageDebt"
              data-field-key="currentHouseMortgageDebt"
            >
              <NumberField
                {...InputConfiguration.money.props}
                leading={FormInputDisplayConfiguration.money.leading}
                value={
                  formValues.currentHouseMortgageDebt &&
                  formValues.currentHouseMortgageDebt !== ""
                    ? Number(formValues.currentHouseMortgageDebt)
                    : undefined
                }
                onChange={(value) =>
                  updateFormValues({
                    currentHouseMortgageDebt: value?.toString() ?? "",
                  })
                }
                validation={["required"]}
                name={t("maxMortgageWizard.purpose.remainingMortgageName")}
                label={t("maxMortgageWizard.purpose.remainingMortgageLabel")}
              />
            </FormRow>

            <FormRow
              id="wizard__currentHouseMortgageLender"
              data-field-key="currentHouseMortgageLender"
            >
              <Select
                search
                value={formValues.currentHouseMortgageLender ?? ""}
                onChange={(value) =>
                  updateFormValues({
                    currentHouseMortgageLender: value?.toString() ?? "",
                  })
                }
                options={banksLoading ? [] : bankOptions}
                validation={["required"]}
                display={
                  formValues.currentHouseMortgageLender === "other"
                    ? t("maxMortgageWizard.lenderOther")
                    : (formValues.currentHouseMortgageLender ?? "")
                }
                name={t("maxMortgageWizard.lenderName")}
                label={t("maxMortgageWizard.lenderLabel")}
              />
            </FormRow>
          </Grid>
        </Flex>
      )}
    </BaseSlide>
  );
};
